/**
 * AutoFillPanel — plain-English "Auto-fill" picker for a fillable-form field.
 * Stores a {{token}} in field.default_value; `no_autofill` opts a field out of
 * the label-based guess that runs when a form is started.
 */
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Wand2 } from "lucide-react";
import { LIVING_SITUATION_GROUPS } from "@/lib/livingSituation";

export const AUTOFILL_GROUPS = [
  {
    label: "Client",
    options: [
      ["{{client_name}}", "Client's name"],
      ["{{client_first_name}}", "Client's first name"],
      ["{{client_last_name}}", "Client's last name"],
      ["{{client_dob}}", "Client's date of birth"],
      ["{{client_ssn}}", "Client's Social Security number"],
      ["{{client_ssn_last4}}", "Client's SSN — last 4 only"],
      ["{{client_address}}", "Client's address"],
      ["{{client_phone}}", "Client's phone"],
      ["{{client_medicaid_id}}", "Client's Medicaid ID"],
      ["{{client_medicare_id}}", "Client's Medicare ID"],
      ["{{client_diagnosis}}", "Client's primary diagnosis"],
      ["{{client_allergies}}", "Client's allergies"],
      ["{{client_physician}}", "Client's physician"],
      ["{{client_physician_phone}}", "Physician phone"],
      ["{{client_emergency_contact}}", "Emergency contact"],
    ],
  },
  {
    label: "Visit",
    options: [
      ["{{client_pcw_name}}", "Assigned caregiver (PCW) name"],
      ["{{rn_name}}", "RN / nurse name"],
      ["{{visit_date}}", "Visit date"],
      ["{{visit_time}}", "Visit time"],
      ["{{today}}", "Today's date"],
    ],
  },
  {
    label: "Agency",
    options: [
      ["{{agency_name}}", "Agency name"],
      ["{{agency_address}}", "Agency address"],
      ["{{agency_phone}}", "Agency phone"],
      ["{{agency_fax}}", "Agency fax"],
      ["{{agency_npi}}", "Agency NPI"],
      ["{{agency_medicaid_id}}", "Agency Medicaid provider #"],
      ["{{agency_ein}}", "Agency EIN / Tax ID"],
    ],
  },
  {
    label: "Employee (HR / onboarding forms)",
    options: [
      ["{{employee_name}}", "Employee name"],
      ["{{employee_legal_first_name}}", "Employee legal first name"],
      ["{{employee_legal_last_name}}", "Employee legal last name"],
      ["{{employee_middle_initial}}", "Employee middle initial"],
      ["{{employee_other_last_names}}", "Other last names used"],
      ["{{employee_address_street}}", "Employee street address"],
      ["{{employee_address_apt}}", "Employee apt. number"],
      ["{{employee_address_city}}", "Employee city"],
      ["{{employee_address_state}}", "Employee state"],
      ["{{employee_address_zip}}", "Employee ZIP"],
      ["{{employee_dob}}", "Employee date of birth"],
      ["{{employee_ssn}}", "Employee SSN"],
      ["{{employee_email}}", "Employee email"],
      ["{{employee_phone}}", "Employee phone"],
      ["{{employee_start_date}}", "Employee start date"],
    ],
  },
];

export const CHECKBOX_GROUPS = [
  {
    label: "Client",
    options: [
      ["{{client_is_male}}", "Client is male"],
      ["{{client_is_female}}", "Client is female"],
      ["{{client_has_medicaid}}", "Client has Medicaid"],
      ["{{client_has_medicare}}", "Client has Medicare"],
      ["{{client_is_diabetic}}", "Client is diabetic"],
      ["{{client_has_seizures}}", "Client has seizure history"],
      ["{{client_has_allergies}}", "Client has allergies"],
    ],
  },
  {
    label: "Visit",
    options: [
      ["{{visit_is_admission}}", "Visit is an Admission"],
      ["{{visit_is_supervisory}}", "Visit is a Supervisory visit"],
      ["{{visit_is_readmission}}", "Visit is a Re-Admission"],
    ],
  },
  {
    label: "Assessor",
    options: [
      ["{{rn_is_rn}}", "Assessor is an RN"],
      ["{{rn_is_lpn}}", "Assessor is an LPN"],
      ["{{rn_is_case_manager}}", "Assessor is a Case Manager"],
    ],
  },
  {
    label: "Living situation (PCST)",
    options: [
      ...LIVING_SITUATION_GROUPS.filter((g) => g.label !== "Other").flatMap((g) => g.options.map(([code, label]) => [`{{client_lives_${code}}}`, `${g.label} — ${label}`])),
      ["{{client_lives_other}}", "Other living situation"],
      ["{{client_lives_own_home}}", "Lives in own home / apartment (any)"],
      ["{{client_lives_others_home}}", "Lives in someone else's home (any)"],
      ["{{client_lives_apartment_services}}", "Lives in an apartment with services (any)"],
      ["{{client_lives_group_residential}}", "Lives in a group residential setting (any)"],
      ["{{client_lives_in_facility}}", "Lives in a facility (any)"],
    ],
  },
  { label: "Other", options: [["{{always}}", "Always checked"]] },
];

const TOKEN_LABEL = Object.fromEntries([...AUTOFILL_GROUPS, ...CHECKBOX_GROUPS].flatMap((g) => g.options));
const SINGLE_TOKEN = /^\{\{\s*[a-z0-9_]+\s*\}\}$/;

export function autoFillLabel(token) {
  if (!token) return "";
  const t = token.trim().replace(/\s+/g, "");
  return TOKEN_LABEL[t] || t.replace(/[{}]/g, "").replace(/_/g, " ");
}

/** Effective auto-fill for a field: {label, detected} or null. */
export function fieldAutoFill(field, guess) {
  if (!field || field.assignee === "hr" || !["text", "date", "checkbox"].includes(field.type)) return null;
  const dv = (field.default_value || "").trim();
  if (dv) return { label: SINGLE_TOKEN.test(dv) ? autoFillLabel(dv) : dv, detected: false };
  if (!field.no_autofill && guess) return { label: autoFillLabel(guess), detected: true };
  return null;
}

export default function AutoFillPanel({ field, guess, onChange }) {
  const isCheckbox = field.type === "checkbox";
  const groups = isCheckbox ? CHECKBOX_GROUPS : AUTOFILL_GROUPS;
  const dv = (field.default_value || "").trim();
  const selectValue = dv
    ? SINGLE_TOKEN.test(dv) ? dv.replace(/\s+/g, "") : "__custom__"
    : field.no_autofill || !guess ? "__none__" : "__auto__";

  const pick = (v) => {
    if (v === "__none__") onChange({ default_value: "", no_autofill: true });
    else if (v === "__auto__") onChange({ default_value: "", no_autofill: false });
    else if (v === "__custom__") onChange({ no_autofill: false });
    else onChange({ default_value: v, no_autofill: false });
  };

  return (
    <div className="space-y-1.5 rounded-md border border-emerald-200 bg-emerald-50 p-2" data-testid="autofill-panel">
      <label className="text-[11px] font-medium text-emerald-800 flex items-center gap-1">
        <Wand2 className="w-3 h-3" /> {isCheckbox ? "Auto-check this box when…" : "Auto-fill this field with"}
      </label>
      <Select value={selectValue} onValueChange={pick}>
        <SelectTrigger className="h-8 text-xs bg-white" data-testid="autofill-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {guess && (
            <SelectItem value="__auto__" className="text-xs" data-testid="autofill-option-auto">
              Auto-detected: {autoFillLabel(guess)}
            </SelectItem>
          )}
          <SelectItem value="__none__" className="text-xs" data-testid="autofill-option-none">
            {isCheckbox ? "Never — ticked by hand" : "None — filled in by hand"}
          </SelectItem>
          {!isCheckbox && <SelectItem value="__custom__" className="text-xs">Custom text…</SelectItem>}
          {groups.map((g) => (
            <SelectGroup key={g.label}>
              <SelectSeparator />
              <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-400">{g.label}</SelectLabel>
              {g.options.map(([tok, label]) => (
                <SelectItem key={tok} value={tok} className="text-xs" data-testid={`autofill-option-${tok.replace(/[{}]/g, "")}`}>
                  {label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      {selectValue === "__auto__" && (
        <p className="text-[10px] text-emerald-700" data-testid="autofill-detected-hint">
          Detected from the label “{field.label}”. It fills in automatically when the form is started — pick a value above to override, or “None” to turn it off.
        </p>
      )}
      {selectValue === "__none__" && (
        <p className="text-[10px] text-slate-500">{isCheckbox ? "Left blank; the nurse ticks it if it applies." : "Nothing is pre-filled; the person completing the form types it."}</p>
      )}
      {!isCheckbox && (selectValue === "__custom__" || SINGLE_TOKEN.test(dv)) && (
        <Input
          className="h-7 text-[11px] bg-white"
          value={field.default_value || ""}
          onChange={(e) => onChange({ default_value: e.target.value, no_autofill: false })}
          placeholder="Custom text or combine tokens, e.g. {{client_last_name}}, {{client_first_name}}"
          data-testid="default-value-input"
        />
      )}
      {selectValue !== "__none__" && selectValue !== "__auto__" && (
        <p className="text-[10px] text-emerald-700">{isCheckbox ? "Ticked automatically when the condition is true for that client / visit; otherwise left blank. The nurse can still change it." : "Pre-filled when the form is started; the nurse or employee can still edit it."}</p>
      )}
    </div>
  );
}
